import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Search,
  TrendingUp,
  Users,
  UserPlus,
  Activity,
  Clock,
  Mail,
  Phone,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { analyticsService } from '../service/analyticsService';

export default function GlobalTrip() {
  // ✅ Calculate current month start and current date dynamically
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    return {
      startDate: `${year}-${month}-01`,
      endDate: `${year}-${month}-${day}`
    };
  };

  const { startDate: defaultStart, endDate: defaultEnd } = getCurrentMonthDates();
  
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filteredMetrics, setFilteredMetrics] = useState([]);
  const [filteredUserGrowth, setFilteredUserGrowth] = useState([]);
  const [filteredUserActivity, setFilteredUserActivity] = useState([]);
  const [filteredUserEngagement, setFilteredUserEngagement] = useState([]);
  const [filteredUserStatus, setFilteredUserStatus] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const transformMetrics = (backendMetrics) => {
    if (!backendMetrics) return [];

    return [
      {
        title: 'Total Users',
        value: backendMetrics.totalUsers?.toLocaleString() || '0',
        change: backendMetrics.totalUsersChange || '0%',
        icon: <Users className="w-6 h-6" />,
        color: 'purple',
        positive: !backendMetrics.totalUsersChange?.startsWith('-'),
      },
      {
        title: 'Active Users Today',
        value: backendMetrics.activeUsersToday?.toLocaleString() || '0',
        change: backendMetrics.activeUsersTodayChange || '0%',
        icon: <Activity className="w-6 h-6" />,
        color: 'pink',
        positive: !backendMetrics.activeUsersTodayChange?.startsWith('-'),
      },
      {
        title: 'New Users This Period',
        value: backendMetrics.newUsersInRange?.toLocaleString() || '0',
        change: backendMetrics.newUsersChange || '0%',
        icon: <UserPlus className="w-6 h-6" />,
        color: 'emerald',
        positive: !backendMetrics.newUsersChange?.startsWith('-'),
      },
      {
        title: 'User Retention Rate',
        value: backendMetrics.retentionRate || '0%',
        change: backendMetrics.retentionChange || '0%',
        icon: <TrendingUp className="w-6 h-6" />,
        color: 'indigo',
        positive: !backendMetrics.retentionChange?.startsWith('-'),
      },
    ];
  };

  const transformUserStatus = (statusDistribution) => {
    if (!statusDistribution) return [];

    return [
      {
        name: 'Active Users',
        value: statusDistribution.active || 0,
        color: '#10B981',
      },
      {
        name: 'Inactive Users',
        value: statusDistribution.inactive || 0,
        color: '#F59E0B',
      },
      {
        name: 'Suspended',
        value: statusDistribution.suspended || 0,
        color: '#EF4444',
      },
    ];
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('📊 Fetching analytics for:', startDate, 'to', endDate);

      const result = await analyticsService.getAllAnalytics(startDate, endDate);
      console.log('📦 Service Result:', result);

      if (result.success) {
        const { data } = result;
        console.log('✅ Data received:', data);

        setFilteredMetrics(transformMetrics(data.metrics));
        setFilteredUserGrowth(data.userGrowth || []);
        setFilteredUserActivity(data.userActivity || []);
        setFilteredUserEngagement(data.userEngagement || []);
        setFilteredUserStatus(transformUserStatus(data.metrics?.statusDistribution));
        setFilteredUsers(data.recentUsers || []);
        setAllUsers(data.recentUsers || []);

        console.log('✅ State updated successfully');
      } else {
        console.error('❌ Service error:', result.message);
        setError(result.message);
        
        setFilteredMetrics([]);
        setFilteredUserGrowth([]);
        setFilteredUserActivity([]);
        setFilteredUserEngagement([]);
        setFilteredUserStatus([]);
        setFilteredUsers([]);
        setAllUsers([]);
      }
    } catch (err) {
      console.error('❌ Fetch Error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }
    fetchAnalyticsData();
  };

  const handleTableSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredUsers(allUsers);
      return;
    }

    const searchFiltered = allUsers.filter(
      (item) =>
        item.name?.toLowerCase().includes(value) ||
        item.email?.toLowerCase().includes(value) ||
        item.phone?.toLowerCase().includes(value)
    );

    setFilteredUsers(searchFiltered);
  };

  const getColorClasses = (color) => {
    const colors = {
      indigo: 'bg-indigo-100 text-indigo-600',
      emerald: 'bg-emerald-100 text-emerald-600',
      blue: 'bg-blue-100 text-blue-600',
      red: 'bg-red-100 text-red-600',
      purple: 'bg-purple-100 text-purple-600',
      pink: 'bg-pink-100 text-pink-600',
      orange: 'bg-orange-100 text-orange-600',
      teal: 'bg-teal-100 text-teal-600',
    };
    return colors[color] || colors.indigo;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      Active: 'bg-emerald-100 text-emerald-700',
      Inactive: 'bg-orange-100 text-orange-700',
      Suspended: 'bg-red-100 text-red-700',
    };
    return statusStyles[status] || statusStyles.Active;
  };

  // ✅ Inline Legend Component
  const InlineLegend = ({ items }) => (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200">
          <div
            className="w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-xs font-medium text-gray-700">{item.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-2xl p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">
          User Analytics Dashboard
        </h3>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Date Range Filter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Apply Filter</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing data from{' '}
          <span className="font-semibold">{new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span> to{' '}
          <span className="font-semibold">{new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredMetrics.length > 0 ? (
          filteredMetrics.map((metric, index) => (
            <div key={index} className="glass rounded-xl p-6 card-hover">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${getColorClasses(metric.color)}`}>
                  {metric.icon}
                </div>
                <span
                  className={`text-sm font-semibold ${
                    metric.positive ? 'text-emerald-600' : 'text-red-600'
                  }`}
                >
                  {metric.change}
                </span>
              </div>
              <h4 className="text-sm font-medium text-gray-600 mb-1">
                {metric.title}
              </h4>
              <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
            </div>
          ))
        ) : (
          !loading && (
            <div className="col-span-4 text-center py-8 text-gray-500">
              No metrics available
            </div>
          )
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ✅ Area Chart - User Growth with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              User Growth Trend (Date-wise)
            </h4>
            {filteredUserGrowth.length > 0 && (
              <InlineLegend
                items={[
                  { color: '#4F46E5', label: 'Total Users' },
                  { color: '#10B981', label: 'Active Users' }
                ]}
              />
            )}
          </div>

          {filteredUserGrowth.length > 0 ? (
            <div className="overflow-x-auto">
              <div
                style={{
                  minWidth: `${Math.max(filteredUserGrowth.length * 50, 600)}px`,
                  width: '100%',
                }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={filteredUserGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="displayDate"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="totalUsers"
                      stackId="1"
                      stroke="#4F46E5"
                      fill="#4F46E5"
                      fillOpacity={0.6}
                      name="Total Users"
                    />
                    <Area
                      type="monotone"
                      dataKey="activeUsers"
                      stackId="2"
                      stroke="#10B981"
                      fill="#10B981"
                      fillOpacity={0.6}
                      name="Active Users"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available for selected date range'}
            </div>
          )}
        </div>

        {/* ✅ Pie Chart - User Status with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              User Status Distribution
            </h4>
            {filteredUserStatus.some((item) => item.value > 0) && (
              <InlineLegend
                items={filteredUserStatus.map(item => ({
                  color: item.color,
                  label: item.name
                }))}
              />
            )}
          </div>

          {filteredUserStatus.some((item) => item.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={filteredUserStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {filteredUserStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

        {/* ✅ Combo Chart - User Activity with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              Daily User Activity (Date-wise)
            </h4>
            {filteredUserActivity.length > 0 && (
              <InlineLegend
                items={[
                  { color: '#4F46E5', label: 'Active Users' },
                  { color: '#10B981', label: 'New Users' }
                ]}
              />
            )}
          </div>

          {filteredUserActivity.length > 0 ? (
            <div className="overflow-x-auto">
              <div
                style={{
                  minWidth: `${Math.max(filteredUserActivity.length * 50, 600)}px`,
                  width: '100%',
                }}
              >
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={filteredUserActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="day"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="users"
                      fill="#4F46E5"
                      name="Active Users"
                      radius={[8, 8, 0, 0]}
                    />
                    <Line
                      type="monotone"
                      dataKey="newUsers"
                      stroke="#10B981"
                      strokeWidth={3}
                      name="New Users"
                      dot={{ r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

        {/* ✅ Line Chart - User Engagement with Inline Legend */}
       <div className="glass rounded-2xl p-6">
  <div className="flex items-center justify-between mb-4">
    <div>
      <h4 className="text-lg font-bold text-gray-900">
        User Engagement by Hour
      </h4>
      <p className="text-xs text-gray-500 mt-1">
        Hourly activity distribution for selected period
      </p>
    </div>
    {filteredUserEngagement.length > 0 && (
      <InlineLegend
        items={[
          { color: '#4F46E5', label: 'Active Users' }
        ]}
      />
    )}
  </div>

  {filteredUserEngagement.length > 0 ? (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={filteredUserEngagement}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="hour"
          tick={{ fontSize: 11 }}
          interval={1}
        />
        <YAxis 
          tick={{ fontSize: 11 }}
          allowDecimals={false}
        />
        <Tooltip 
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '12px'
          }}
          labelFormatter={(value) => `Time: ${value}`}
          formatter={(value) => [`${value} users`, 'Active Users']}
        />
        <Line
          type="monotone"
          dataKey="users"
          stroke="#4F46E5"
          strokeWidth={3}
          name="Active Users"
          dot={{ r: 4, fill: '#4F46E5' }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  ) : (
    <div className="h-[300px] flex items-center justify-center text-gray-500">
      {loading ? 'Loading...' : 'No hourly engagement data available for selected date range'}
    </div>
  )}
</div>
      </div>

      {/* Table - Recent User Activity */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-bold text-gray-900">
              Recent User Activity
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              {filteredUsers.length} users joined during selected period
            </p>
          </div>

          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleTableSearch}
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    User
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email / Phone
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Status
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Join Date
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Last Active
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 text-gray-900 font-medium">
                      {user.name}
                    </td>
                    <td className="py-3 px-4">
                      {user.email ? (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{user.email}</span>
                        </div>
                      ) : user.phone ? (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{user.phone}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                          user.status
                        )}`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{user.joinDate}</td>
                    <td className="py-3 px-4 text-gray-600">{user.lastActive}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500">
                    No users found for the selected date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
